import { formatDate } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map, mergeMap, take } from 'rxjs/operators';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import {
  IMAGE_SRC,
  STATUS_OPTIONS,
} from 'src/app/components/shared/shared-constants';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LocationEdwSummaryReport,
  LocationEdwSummaryRequest,
} from 'src/app/core/models/reports/reports.model';
import { IdoReportsService } from 'src/app/core/services/reports/ido/ido-reports.service';
import { OnRoadOperationsService } from 'src/app/core/services/reports/on-road-operations/on-road-operations.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import { MOCK_ROUTES } from '../../../../reports.constants';
import { Options } from '../../../shared/report-format/report-format.constants';
import {
  CONSTS,
  CSV_FILE_COLUMN_HEADING,
  FILE_NAME,
  LocationEdwFormGroup,
  PDF_REPORT,
} from './location-edw.constants';

@Component({
  selector: 'app-location-edw',
  templateUrl: './location-edw.component.html',
  styleUrls: ['./location-edw.component.scss'],
  standalone: false,
})
export class LocationEdwComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly input: LocationEdwFormGroup = inject(MAT_DIALOG_DATA);
  private userService = inject(UserService);
  private fb: FormBuilder = inject(FormBuilder);
  private dialogRef: MatDialogRef<LocationEdwComponent> = inject(
    MatDialogRef<LocationEdwComponent>,
  );
  private onRoadOperationService: OnRoadOperationsService = inject(
    OnRoadOperationsService,
  );
  private idoReportsService: IdoReportsService = inject(IdoReportsService);
  private reportUtils: ReportsUtils = inject(ReportsUtils);
  private commonUtils: CommonUtils = inject(CommonUtils);

  readonly CONSTS = CONSTS;
  readonly IMAGES = IMAGE_SRC;
  readonly PDF_REPORT = PDF_REPORT;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  currDate!: Date;
  minDate!: Date;
  maxDate!: Date;
  locationEdwForm!: FormGroup;
  routes = MOCK_ROUTES;
  location!: string;
  locationEdwSummaryReport!: LocationEdwSummaryReport;

  @ViewChild('pdfContainer', { read: ViewContainerRef })
  pdfContainer!: ViewContainerRef;

  ngOnInit(): void {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        mergeMap((user) => {
          this.currDate = new Date();
          this.minDate = new Date(
            this.currDate.getFullYear(),
            this.currDate.getMonth(),
            this.currDate.getDate() - 30,
          );
          this.maxDate = new Date(
            this.currDate.getFullYear(),
            this.currDate.getMonth(),
            this.currDate.getDate() + 14,
          );

          this.locationEdwForm = this.fb.group({
            date: [this.currDate],
            routes: [MOCK_ROUTES[0]],
            reportFormat: [Options.PDF],
          });

          this.location = user?.location;
          return this.getRouteList(this.location);
        }),
      )
      .subscribe();
  }

  onExit() {
    this.commonUtils.openExitModal(this.locationEdwForm.dirty);
  }

  radioChange(newValue: Options) {
    this.locationEdwForm.get('reportFormat')?.setValue(newValue);
  }

  /**
   * Used to get report in CSV or PDF file format
   *
   * @memberof LocationEdwComponent
   */
  getReport() {
    const locationCode = this.location;
    const formValue = this.locationEdwForm.value;
    const date = this.commonUtils.getFormattedDate(formValue.date);
    const request: LocationEdwSummaryRequest = {
      locationCode,
      date,
      loop:
        formValue.routes?.value === STATUS_OPTIONS.ALL_SELECTED
          ? STATUS_OPTIONS.ALL_SELECTED
          : formValue.routes,
    };
    this.idoReportsService
      .getLocationEdwSummaryReport(request)
      .pipe(take(1))
      .subscribe((data) => {
        const formatType = this.locationEdwForm.get('reportFormat')?.value;

        const locationEDWSummary = data?.response?.locationEDWSummary || [];
        if (locationEDWSummary.length) {
          if (formatType === Options.CSV) {
            const customHeader = [
              [
                '',
                '',
                '',
                '',
                '',
                '',
                'EDW Location Summary',
                '',
                '',
                '',
                '',
                '',
              ],
              [
                '',
                'Site Code',
                `${data?.response.siteCode}`,
                '',
                '',
                'Site Name',
                `${data?.response.siteName}`,
                '',
                '',
                '',
                'Date',
                `${data?.response.date}`,
              ],
            ];
            const formatedDate = formatDate(new Date(), 'MM-dd-yyyy', 'en-US');
            const customFooter = [
              [
                'TOTAL',
                '',
                `${data?.response.totaltotalStops}`,
                `${data?.response.totaltotalPkgs}`,
                `${data?.response.totalnumOfTotalStopswithEDW}`,
                `${data?.response.totalnumOfTotalStopsEDWMet}`,
                `${data?.response.totalpercentOfTotalStopsEDWMet}`,
                `${data?.response.totalnumOfCommittedStopswithEDW}`,
                `${data?.response.totalnumOfCommittedStopsEDWMet}`,
                `${data?.response.totalpercentOfCommittedStopsEDWMet}`,
              ],
              ['Printed:', `${formatedDate}`],
            ];
            this.reportUtils.exportCSVReport(
              locationEDWSummary,
              FILE_NAME,
              customHeader,
              customFooter,
              CSV_FILE_COLUMN_HEADING,
            );
          }
          if (formatType === Options.PDF) {
            this.locationEdwSummaryReport = data;
            setTimeout(() => {
              const content = this.pdfContainer.element.nativeElement.innerHTML;
              this.reportUtils.exportPdfReport(
                content,
                PDF_REPORT.NAME,
                FILE_NAME,
                'l',
              );
            }, 0);
          }
        } else {
          this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
        }
        this.dialogRef.close();
      });
  }

  /**
   * Used to get route list
   * driver load routes
   *
   * @private
   * @memberof LocationEdwComponent
   */
  private getRouteList(code: string): Observable<void> {
    const locationCode = code;
    return this.onRoadOperationService.getDriverLoadRoutes(locationCode).pipe(
      take(1),
      map((data) => {
        this.routes = data.response.dLRoutes
          .map(({ route }) => {
            return {
              label: route,
              value: route,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label));
        this.routes.unshift({
          label: STATUS_OPTIONS.ALL_SELECTED,
          value: 'All',
        });
      }),
    );
  }
}
